import {riskFormAnalyticsAllowList} from '@stripe-internal/risk-form';

const riskTelemetryAnalyticsParams: Record<string, true> = {};

riskFormAnalyticsAllowList.forEach((key) => {
  riskTelemetryAnalyticsParams[key] = true;
});

const arkAnalyticsParams = {
  ark_version: true,
  analytics_name: true,
  additional_analytics: true,

  // IDs
  id: true,
  file_id: true,
  person_id: true,
  entity_token: true,
  object_id: true,

  // Track the number of persons that were visible on a prefill page
  num_prefilled_directors: true,
  num_existing_directors: true,
  num_prefilled_owners: true,
  num_existing_owners: true,

  // Errors
  request_id: true,
  error: true,
  error_codes: true,
  error_code: true,
  validation_errors: true,
  previous_validation_errors: true,
  error_expected: true,
  error_path: true,
  error_muted: true,
  error_source: true,
  reason: true,

  // Network
  network_action_id: true,
  network_duration: true,
  network_request_id: true,
  network_status: true,
  network_trace_id: true,

  // Operation
  operation_deduplicate: true,
  operation_prefetch: true,
  operation_prefetch_hit: true,

  // Account information
  country: true,
  nationality: true,
  capabilities: true,
  business_type: true,
  business_structure: true,
  company_structure: true,
  industry: true,
  tos: true,
  requirements: true,
  outstanding_requirements: true,
  section_outstanding_requirements: true,
  connected_merchant: true,
  client_application_id: true,
  terms_of_service: true,
  unsupportedRequirements: true,
  isV2AccountSession: true,
  recipient_id: true,
  accountId: true,
  compartmentId: true,

  // Account traits
  hasStandardDashboardAccess: true,
  hasExpressDashboardAccess: true,
  stripeOwnsLossLiability: true,
  stripeOwnsPricing: true,
  stripeOwnsOnboarding: true,

  // Flow
  previous_step_name: true,
  previous_composition_name: true,
  step_name: true,
  composition_name: true,
  is_dashboard_form_composition: true,
  is_deferred_submission: true,
  success: true,
  composition_path: true,
  section_name: true,
  section_view: true,
  flow: true,
  step: true,
  multi_step_flow_name: true,
  externalStepName: true,
  can_proceed: true,
  is_mobile: true,
  flow_intent: true,
  flow_satisfaction_criteria: true,
  collect_future_requirements: true,
  api: true,
  frontend: true,
  ark_fields: true,
  spec_is_applicable: true,
  spec_is_completed: true,
  spec_requirements_collected: true,
  spec_fields_shown: true,
  computed_is_applicable: true,
  computed_is_completed: true,
  current_requirements: true,
  current_future_requirements: true,
  requirement_restrictions: true,
  capability_restriction: true,
  capability_restrictions: true,
  shown_requirements: true,
  form_id: true,
  field: true,
  device: true,

  // Document uploads
  field_name: true,
  selected_document_type: true,
  upload_source: true,
  blur_score: true,
  darkness_score: true,
  blur_time_millis: true,
  darkness_time_millis: true,

  // Flags
  is_auto_request_afterpay_capab_enabled: true,
  is_auto_request_affirm_capab_enabled: true,
  is_auto_request_link_capab_enabled: true,
  is_auto_request_us_bank_account_capab_enabled: true,
  is_auto_request_cashapp_capab_enabled: true,
  is_auto_request_apple_pay_later_capab_enabled: true,

  // Experiments
  experiment_retrieved: true,
  experiment_treatment_group: true,
  is_in_preloaded: true,
  experiment_checked_by: true,
  dashboard_type: true, // used by the statement_descriptor_llm_suggestions and statement_descriptor_llm_suggestions_aa experiments
  'dimensions-platform_id': true,
  'dimensions-account_configuration': true,

  // Suggestions
  ark_field_errors: true,
  ark_fields_count: true,
  suggestion_type: true,
  provenance: true,

  // Bank account collection discrepancy tracking (temp: https://jira.corp.stripe.com/browse/CRE-2504)
  api_value: true,
  actual_decision: true,
  onboarding_owner: true,
  location: true,

  // External account form
  bank_account_form_initial_country: true,
  bank_account_form_type: true,
  bank_account_form_use_case: true,
  external_account_form_use_case: true,
  external_account_currency: true,
  external_account_country: true,
  merchant_default_currency: true,
  submit_action_type: true,
  can_retry: true,
  has_challenge_digest: true,
  external_account_type: true,
  error_message_code: true,

  // Capital application parameters
  verification_status: true,
  verification_status_count: true,
  verification_previously_failed: true,
  previous_verification_status: true,
  synchronous_verification_enabled: true,

  // Shadow Test
  original: true,
  new: true,
  difference: true,
  unexpected_caps: true,
  shadow_mode: true,

  // Alternative Requirements
  message: true,
  alternative: true,
  supportedAlternatives: true,

  // The verification methods available to the user on a given page
  verification_methods: true,

  // Relationship Verification method picker instrumentation
  multi_step_flow_id: true,
  method: true,
  previous_method: true,

  // LE sharing analytics
  selected_legal_entity: true,
  has_outstanding_requirements: true,
  is_one_click: true,
};

const performanceMonitorAnalyticsParams: Record<string, true> = {
  // TTN durations
  duration_inclusive: true,
  duration_exclusive: true,

  // Start/stop reasons
  start_reason: true,
  stop_reason: true,

  // Render counts & durations
  render_count: true,
  render_count_inclusive: true,
  average_render_duration: true,
  first_render_name: true,
  first_render_duration: true,
  last_render_name: true,
  last_render_duration: true,
  longest_render_name: true,
  longest_render_duration: true,
  longest_render_name_inclusive: true,
  longest_render_duration_inclusive: true,
  shortest_render_name: true,
  shortest_render_duration: true,
  shortest_render_name_inclusive: true,
  shortest_render_duration_inclusive: true,

  // Track (query/script) counts & durations
  track_count: true,
  track_count_inclusive: true,
  average_track_duration: true,
  first_track_name: true,
  first_track_duration: true,
  last_track_name: true,
  last_track_duration: true,
  longest_track_name: true,
  longest_track_duration: true,
  longest_track_name_inclusive: true,
  longest_track_duration_inclusive: true,
  shortest_track_name: true,
  shortest_track_duration: true,
  shortest_track_name_inclusive: true,
  shortest_track_duration_inclusive: true,

  // Other
  pending_tracks: true,
  data: true,
  measure: true,
  sequence: true,
  event_count: true,
};

export const submerchantAnalyticsParams = {
  // This list controls the dimensions that are sent through analytics. If the parameter is not on this list, it will be [Redacted]
  // https://trailhead.corp.stripe.com/docs/ark/analytics#adding-properties
  // Note that this allows the parameter to go to AEL, however to pipe it through for realtime monitoring (detectors, grafana) it needs
  // to be added to https://go/ael-config
  connectElement: true,
  uiLayerId: true,
  api_result: true,
  queryKey: true,
  livemode: true,
  duration: true,
  platform_id: true,
  connected_account_id: true,
  recommended_business_type: true,
  recommended_company_structure: true,
  selected_business_type: true,
  selected_company_structure: true,
  changed_from_recommendation: true,
  business_type_recommendation_renderer: true,
  suggestions_duration_ms: true,
  suggestions_implementation: true,
  suggestions_status: true,
  sync_duration_ms: true,
  sync_status: true,
  stripe_email_id: true,
  stripe_target_component: true,
  connectionEffectiveType: true,
  initializationPattern: true,
  isInTopLevelIframe: true,
  eventListeners: true,
  platformHostName: true,
  connectJsPath: true,
  type: true,
  htmlAttributes: true,
  loadedFontFamilies: true,
  browserSecureContext: true,
  platformOrigin: true,
  specifiedFontFamiliesCount: true,
  release: true,
  api_response_code: true,
  api_request_url: true,
  api_request_method: true,
  component: true,
  headlessComponent: true,
  uuid: true,
  networkDuration: true,
  page: true,
  runtime_overhead: true,
  operation_name: true,

  // Locale metadata
  attemptedLocale: true,
  matchedLocale: true,
  localeSource: true,
  localeMatch: true,

  // connectJsOptions metadata
  connectJsOptions: true,
  optimizedLoading: true,
  hostApp: true,

  // component attributes
  url: true,
  serviceAgreement: true,

  // Error/debugging fields
  stripeRequestId: true,
  stripeErrorRawType: true,
  stripeStatusCode: true,
  stripeStatusCodeRange: true,
  stripeErrorCode: true,
  stripeErrorMessage: true,
  teamName: true,
  teamOwner: true,
  error_message: true,
  error_stack: true,
  component_stack: true,
  service: true,
  project: true,
  errorCode: true,
  errorMessage: true,
  errorPath: true,
  errorExpected: true,
  errorMuted: true,
  errorSource: true,
  operationDeduplicate: true,
  operationName: true,
  operationPrefetch: true,
  networkActionId: true,
  networkRequestId: true,
  networkTraceId: true,
  networkStatus: true,
  version: true,
  setter: true,
  typeOfValue: true,
  jsonStringifiedValue: true,

  // Temporal, for testing purposes only
  _tdr_id: true,

  // connectjs sdk params
  sdk: true,
  sdkVersion: true,
  reactSdkVersion: true,

  // Mobile sdk params
  mobileSdk: true,
  mobileSdkVersion: true,
  mobileSdkBundleAppId: true,
  mobileSdkDurableFlow: true,

  // Mobile sdk authed webview params
  invokingComponent: true,

  // Inherited font family
  inheritedFontFamily: true,

  // Custom fonts
  fontCount: true,
  fontLoadTime: true,
  fontCssSrc: true,
  fontSrc: true,
  platformLoadedFont: true,
  iframeLoadedFont: true,
  availableInIframe: true,

  // Payout Schedule Form
  account_id: true,
  payout_setting_schedule_preference: true,
  payout_setting_schedule_interval: true,

  // Payouts
  payoutMethod: true,
  payout_id: true,
  promotionShown: true,

  // Capital embedded components
  layoutMode: true,
  linkingScenario: true,
  offer_id: true,
  offer_application_id: true,
  offer_bundle_token: true,
  offer_page_layout: true,
  product_variant: true,
  financing_type: true,
  financing_id: true,
  financing_state: true,
  capital_scheme: true,
  has_completed_onboarding: true,
  current_terms: true,
  multi_draw_line_id: true,
  view_state: true,
  faq_item: true,
  default_financing_offer: true,
  show_financing_selector: true,
  presentation_mode: true,

  // app-ui-extension-runtime
  sdk_version: true,
  app_id: true,
  app_version: true,
  platform: true,
  app_channel: true,
  component_name: true,
  tracked_props: true,

  // Payment method
  payment_method: true,

  // Payment method settings
  paymentMethod: true,
  capability: true,
  requestedCapability: true,
  updatedConfiguration: true,
  displayValue: true,
  requiresKYC: true,

  // Finrep analytics
  report_type_ids: true,
  duration_ms: true,
  source: true,
  preset: true,
  from: true,
  to: true,
  from_start: true,
  from_end: true,
  to_start: true,
  to_end: true,

  // Disputes enriched canonical search parity check
  is_full_match: true,
  is_obo_enabled: true,
  total_count_match: true,
  has_more_match: true,
  id_order_match: true,
  missing_ids_count: true,
  extra_ids_count: true,
  field_mismatches_count: true,
  field_mismatch_fields: true,
  original_count: true,
  enriched_count: true,
  dispute_id: true,
  original_evidence_details_due_by: true,
  enriched_evidence_details_due_by: true,
  original_auto_submission_method: true,
  enriched_auto_submission_method: true,
  original_status: true,
  enriched_status: true,

  // Smart Disputes submit eligibility (useCanSubmitUsingSmartDisputes hook)
  merchant_id: true,
  can_submit_using_smart_disputes: true,
  smart_disputes_management_enabled: true,
  is_merchant_enrolled_in_auto_representment: true,
  is_dispute_auto_representable: true,
  disable_smart_disputes_management_embedded_components: true,
  enable_smart_disputes_management_feature_cutover: true,

  ...arkAnalyticsParams,
  ...riskTelemetryAnalyticsParams,
  ...performanceMonitorAnalyticsParams,
};
